import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEAD_COLLECTION_SOURCE_OPTIONS,
  LEAD_CONSENT_STATUS_OPTIONS,
  LEAD_LAWFUL_BASIS_OPTIONS,
  LEAD_PRIVACY_NOTICE_STATUS_OPTIONS,
  LEAD_STATUS_OPTIONS,
} from '../../../config/enum-options.js';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';

function renderOptions(options, t, namespace) {
  return options.map((value) => (
    <option key={value} value={value}>
      {getEnumOptionLabel(t, namespace, value)}
    </option>
  ));
}

function LeadForm({
  form,
  formErrors,
  error,
  isSaving,
  isLoadingDetails,
  countryOptions,
  onChange,
  onAddDetail,
  onRemoveDetail,
  onSubmit,
  onCancel,
  bodyClassName = '',
  actionsClassName = 'd-flex justify-content-end gap-2 mt-4',
}) {
  const { t } = useTranslation();
  const onFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    onChange(name, type === 'checkbox' ? checked : value);
  };

  return (
    <form data-testid="lead-form" onSubmit={onSubmit}>
      <div className={bodyClassName}>
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        {isLoadingDetails ? <div className="alert alert-info py-2">{t('common.loading')}</div> : null}
        {formErrors.contract ? <div className="alert alert-warning py-2">{t('leads.updateRequiredFields')}</div> : null}

        <h6 className="mb-3">{t('leads.coreInformation')}</h6>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label className="form-label">{t('profiles.firstName')}</label>
            <input className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`} name="firstName" value={form.firstName} onChange={onFieldChange} maxLength="128" />
          </div>
          <div className="col-md-6">
            <label className="form-label">{t('profiles.lastName')}</label>
            <input className={`form-control ${formErrors.lastName ? 'is-invalid' : ''}`} name="lastName" value={form.lastName} onChange={onFieldChange} maxLength="128" />
          </div>
          <div className="col-md-6">
            <label className="form-label">{t('common.email')}</label>
            <input
              className={`form-control ${formErrors.contact || formErrors.email ? 'is-invalid' : ''}`}
              name="email"
              type="email"
              value={form.email}
              onChange={onFieldChange}
              maxLength="128"
            />
            {formErrors.contact ? <div className="invalid-feedback">{t('leads.contactRequired')}</div> : null}
          </div>
          <div className="col-md-6">
            <label className="form-label">{t('leads.phone')}</label>
            <input
              className={`form-control ${formErrors.contact || formErrors.phone ? 'is-invalid' : ''}`}
              name="phone"
              value={form.phone}
              onChange={onFieldChange}
              maxLength="16"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('profiles.country')}</label>
            <select className={`form-select ${formErrors.country ? 'is-invalid' : ''}`} name="country" value={form.country} onChange={onFieldChange}>
              <option value="">{t('leads.anyCountry')}</option>
              {countryOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('leads.language')}</label>
            <input className="form-control" name="language" value={form.language} onChange={onFieldChange} maxLength="2" placeholder="en" />
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('common.status')}</label>
            <select className="form-select" name="status" value={form.status} onChange={onFieldChange}>
              {renderOptions(LEAD_STATUS_OPTIONS, t, 'leadStatus')}
            </select>
          </div>
        </div>

        <div className="alert alert-secondary py-3">
          <div className="fw-semibold mb-1">{t('leads.gdprHelpTitle')}</div>
          <div className="small text-muted">{t('leads.gdprHelpText')}</div>
        </div>

        <h6 className="mb-3">{t('leads.gdprInformation')}</h6>
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <label className="form-label">{t('leads.collectionSource')}</label>
            <select className="form-select" name="collectionSource" value={form.collectionSource} onChange={onFieldChange}>
              {renderOptions(LEAD_COLLECTION_SOURCE_OPTIONS, t, 'leadCollectionSource')}
            </select>
            <div className="form-text">{t('leads.collectionSourceHelp')}</div>
          </div>
          <div className="col-md-6">
            <label className="form-label">{t('leads.collectionContext')}</label>
            <input className="form-control" name="collectionContext" value={form.collectionContext} onChange={onFieldChange} />
            <div className="form-text">{t('leads.collectionContextHelp')}</div>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('leads.lawfulBasis')}</label>
            <select className="form-select" name="lawfulBasis" value={form.lawfulBasis} onChange={onFieldChange}>
              {renderOptions(LEAD_LAWFUL_BASIS_OPTIONS, t, 'leadLawfulBasis')}
            </select>
            <div className="form-text">{t('leads.lawfulBasisHelp')}</div>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('leads.consentStatus')}</label>
            <select className="form-select" name="consentStatus" value={form.consentStatus} onChange={onFieldChange}>
              {renderOptions(LEAD_CONSENT_STATUS_OPTIONS, t, 'leadConsentStatus')}
            </select>
            <div className="form-text">{t('leads.consentStatusHelp')}</div>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('leads.privacyNoticeStatus')}</label>
            <select className="form-select" name="privacyNoticeStatus" value={form.privacyNoticeStatus} onChange={onFieldChange}>
              {renderOptions(LEAD_PRIVACY_NOTICE_STATUS_OPTIONS, t, 'leadPrivacyNoticeStatus')}
            </select>
            <div className="form-text">{t('leads.privacyNoticeHelp')}</div>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t('leads.privacyNoticeSentAt')}</label>
            <input className="form-control" name="privacyNoticeSentAt" type="datetime-local" value={form.privacyNoticeSentAt} onChange={onFieldChange} />
          </div>
          <div className="col-md-8">
            <div className="d-flex flex-wrap gap-4 pt-md-4">
              <div className="form-check form-switch">
                <input id="leadMarketingConsentEmail" className="form-check-input" type="checkbox" name="marketingConsentEmail" checked={form.marketingConsentEmail} onChange={onFieldChange} />
                <label className="form-check-label" htmlFor="leadMarketingConsentEmail">{t('leads.marketingConsentEmail')}</label>
                <div className="form-text">{t('leads.marketingConsentHelp')}</div>
              </div>
              <div className="form-check form-switch">
                <input id="leadMarketingConsentPhone" className="form-check-input" type="checkbox" name="marketingConsentPhone" checked={form.marketingConsentPhone} onChange={onFieldChange} />
                <label className="form-check-label" htmlFor="leadMarketingConsentPhone">{t('leads.marketingConsentPhone')}</label>
              </div>
              <div className="form-check form-switch">
                <input id="leadDoNotContactForm" className="form-check-input" type="checkbox" name="doNotContact" checked={form.doNotContact} onChange={onFieldChange} />
                <label className="form-check-label" htmlFor="leadDoNotContactForm">{t('leads.doNotContact')}</label>
                <div className="form-text">{t('leads.doNotContactHelp')}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">{t('leads.additionalDetails')}</h6>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onAddDetail}>
            <i className="ti ti-plus me-1"></i>
            {t('leads.addDetail')}
          </button>
        </div>
        {formErrors.details ? <div className="text-danger small mb-2">{t('leads.detailPairRequired')}</div> : null}
        {(form.details || []).map((detail, index) => (
          <div className="row g-2 mb-2" key={`lead-detail-${index}`}>
            <div className="col-md-5">
              <input className="form-control" name={`details.${index}.name`} value={detail.name} onChange={onFieldChange} placeholder={t('leads.detailName')} maxLength="128" />
            </div>
            <div className="col-md-6">
              <input className="form-control" name={`details.${index}.value`} value={detail.value} onChange={onFieldChange} placeholder={t('leads.detailValue')} maxLength="1024" />
            </div>
            <div className="col-md-1">
              <button type="button" className="btn btn-outline-danger w-100" onClick={() => onRemoveDetail(index)} aria-label={t('common.delete')}>
                <i className="ti ti-trash"></i>
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className={actionsClassName}>
        <button type="button" className="btn btn-default" onClick={onCancel} disabled={isSaving}>{t('common.cancel')}</button>
        <button type="submit" className="btn btn-primary" disabled={isSaving || isLoadingDetails}>
          {isSaving ? t('common.loading') : t('leads.saveLead')}
        </button>
      </div>
    </form>
  );
}

export default LeadForm;
